/**
 * WooCommerce E-commerce Integration
 * Implements comprehensive WooCommerce integration with REST API client,
 * customer and order synchronization, product catalog sync, and cart abandonment handling
 */

import { BaseEcommerceIntegration } from '../base/BaseEcommerceIntegration';
import {
  IntegrationConfig,
  ValidationResult,
  TestResult,
  ConnectionResult
} from '../../../types/integration';
import {
  EcommerceProduct,
  EcommerceCustomer,
  EcommerceOrder,
  RecommendationContext
} from '../base/BaseEcommerceIntegration';
import { APIGateway } from '../../api/APIGateway';
import { AuthenticationService } from '../../api/AuthenticationService';

export interface WooCommerceConfig extends IntegrationConfig {
  siteUrl: string;
  consumerKey: string;
  consumerSecret: string;
  apiVersion: string;
  webhookSecret?: string;
  webhookEndpoint?: string;
  useSSL: boolean;
  timeout: number;
}

export interface WooCommerceProduct {
  id: number;
  name: string;
  slug: string;
  permalink: string;
  date_created: string;
  date_created_gmt: string;
  date_modified: string;
  date_modified_gmt: string;
  type: 'simple' | 'grouped' | 'external' | 'variable';
  status: 'draft' | 'pending' | 'private' | 'publish';
  featured: boolean;
  catalog_visibility: 'visible' | 'catalog' | 'search' | 'hidden';
  description: string;
  short_description: string;
  sku: string;
  price: string;
  regular_price: string;
  sale_price: string;
  date_on_sale_from?: string;
  date_on_sale_from_gmt?: string;
  date_on_sale_to?: string;
  date_on_sale_to_gmt?: string;
  price_html: string;
  on_sale: boolean;
  purchasable: boolean;
  total_sales: number;
  virtual: boolean;
  downloadable: boolean;
  downloads: any[];
  download_limit: number;
  download_expiry: number;
  external_url: string;
  button_text: string;
  tax_status: 'taxable' | 'shipping' | 'none';
  tax_class: string;
  manage_stock: boolean;
  stock_quantity?: number;
  stock_status: 'instock' | 'outofstock' | 'onbackorder';
  backorders: 'no' | 'notify' | 'yes';
  backorders_allowed: boolean;
  backordered: boolean;
  sold_individually: boolean;
  weight: string;
  dimensions: {
    length: string;
    width: string;
    height: string;
  };
  shipping_required: boolean;
  shipping_taxable: boolean;
  shipping_class: string;
  shipping_class_id: number;
  reviews_allowed: boolean;
  average_rating: string;
  rating_count: number;
  related_ids: number[];
  upsell_ids: number[];
  cross_sell_ids: number[];
  parent_id: number;
  purchase_note: string;
  categories: WooCommerceCategory[];
  tags: WooCommerceTag[];
  images: WooCommerceImage[];
  attributes: WooCommerceAttribute[];
  default_attributes: any[];
  variations: number[];
  grouped_products: number[];
  menu_order: number;
  meta_data: WooCommerceMetaData[];
}

export interface WooCommerceCategory {
  id: number;
  name: string;
  slug: string;
}

export interface WooCommerceTag {
  id: number;
  name: string;
  slug: string;
}

export interface WooCommerceImage {
  id: number;
  date_created: string;
  date_created_gmt: string;
  date_modified: string;
  date_modified_gmt: string;
  src: string;
  name: string;
  alt: string;
  position: number;
}

export interface WooCommerceAttribute {
  id: number;
  name: string;
  position: number;
  visible: boolean;
  variation: boolean;
  options: string[];
}

export interface WooCommerceMetaData {
  id: number;
  key: string;
  value: any;
}

export interface WooCommerceCustomer {
  id: number;
  date_created: string;
  date_created_gmt: string;
  date_modified: string;
  date_modified_gmt: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  username: string;
  billing: WooCommerceAddress;
  shipping: WooCommerceAddress;
  is_paying_customer: boolean;
  avatar_url: string;
  meta_data: WooCommerceMetaData[];
}

export interface WooCommerceAddress {
  first_name: string;
  last_name: string;
  company: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email?: string;
  phone?: string;
}

export interface WooCommerceOrder {
  id: number;
  parent_id: number;
  number: string;
  order_key: string;
  created_via: string;
  version: string;
  status: 'pending' | 'processing' | 'on-hold' | 'completed' | 'cancelled' | 'refunded' | 'failed' | 'trash';
  currency: string;
  date_created: string;
  date_created_gmt: string;
  date_modified: string;
  date_modified_gmt: string;
  discount_total: string;
  discount_tax: string;
  shipping_total: string;
  shipping_tax: string;
  cart_tax: string;
  total: string;
  total_tax: string;
  prices_include_tax: boolean;
  customer_id: number;
  customer_ip_address: string;
  customer_user_agent: string;
  customer_note: string;
  billing: WooCommerceAddress;
  shipping: WooCommerceAddress;
  payment_method: string;
  payment_method_title: string;
  transaction_id: string;
  date_paid?: string;
  date_paid_gmt?: string;
  date_completed?: string;
  date_completed_gmt?: string;
  cart_hash: string;
  meta_data: WooCommerceMetaData[];
  line_items: WooCommerceLineItem[];
  tax_lines: any[];
  shipping_lines: any[];
  fee_lines: any[];
  coupon_lines: any[];
  refunds: any[];
}

export interface WooCommerceLineItem {
  id: number;
  name: string;
  product_id: number;
  variation_id: number;
  quantity: number;
  tax_class: string;
  subtotal: string;
  subtotal_tax: string;
  total: string;
  total_tax: string;
  taxes: any[];
  meta_data: WooCommerceMetaData[];
  sku: string;
  price: number;
}

export class WooCommerceIntegration extends BaseEcommerceIntegration {
  private apiGateway: APIGateway;
  private authService: AuthenticationService;
  private wooConfig: WooCommerceConfig;
  private baseUrl: string;

  constructor(config: WooCommerceConfig) {
    super(config);
    this.wooConfig = config;
    this.apiGateway = APIGateway.getInstance();
    this.authService = new AuthenticationService();
    this.baseUrl = `${config.siteUrl}/wp-json/wc/${config.apiVersion}`;
  }

  /**
   * Validate WooCommerce configuration
   */
  async validateConfig(): Promise<ValidationResult> {
    const errors: string[] = [];

    if (!this.wooConfig.siteUrl) {
      errors.push('Site URL is required');
    } else if (!this.isValidUrl(this.wooConfig.siteUrl)) {
      errors.push('Invalid site URL format');
    }

    if (!this.wooConfig.consumerKey) {
      errors.push('Consumer key is required');
    }

    if (!this.wooConfig.consumerSecret) {
      errors.push('Consumer secret is required');
    }

    if (!this.wooConfig.apiVersion) {
      errors.push('API version is required');
    }

    return {
      valid: errors.length === 0,
      errors: errors.map(error => ({
        field: 'config',
        message: error,
        code: 'VALIDATION_ERROR'
      })),
      warnings: []
    };
  }

  /**
   * Test connection to WooCommerce store
   */
  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    
    try {
      const response = await this.makeRequest('GET', '/system_status');
      
      if (response.environment) {
        return {
          success: true,
          message: `Successfully connected to WooCommerce store`,
          duration: Date.now() - startTime,
          details: {
            version: response.environment.version,
            wpVersion: response.environment.wp_version,
            phpVersion: response.environment.php_version,
            serverInfo: response.environment.server_info
          }
        };
      }
      
      return {
        success: false,
        message: 'Failed to retrieve system status',
        duration: Date.now() - startTime
      };
    } catch (error) {
      return {
        success: false,
        message: `Connection test failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        duration: Date.now() - startTime
      };
    }
  }

  /**
   * Connect to WooCommerce using REST API credentials
   */
  async connect(): Promise<ConnectionResult> {
    try {
      const testResult = await this.testConnection();
      
      if (testResult.success) {
        await this.registerWebhooks();
        return {
          success: true,
          message: 'Successfully connected to WooCommerce'
        };
      }
      
      return {
        success: false,
        message: testResult.message || 'Failed to connect to WooCommerce'
      };
    } catch (error) {
      return {
        success: false,
        message: `Failed to connect to WooCommerce: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Disconnect from WooCommerce
   */
  async disconnect(): Promise<void> {
    try {
      // Unregister webhooks
      await this.unregisterWebhooks();
      
      await this.emitEvent('woocommerce.disconnected', {});
    } catch (error) {
      await this.emitEvent('woocommerce.disconnect_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Sync products from WooCommerce
   */
  async syncProducts(): Promise<EcommerceProduct[]> {
    try {
      const products: EcommerceProduct[] = [];
      let page = 1;
      const perPage = 100;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await this.makeRequest('GET', '/products', {
          page,
          per_page: perPage,
          status: 'publish'
        });

        if (Array.isArray(response) && response.length > 0) {
          for (const wooProduct of response) {
            const product = this.transformWooCommerceProduct(wooProduct);
            products.push(product);
          }
          
          hasMorePages = response.length === perPage;
          page++;
        } else {
          hasMorePages = false;
        }
      }

      await this.emitEvent('products.synced', { count: products.length });
      return products;
    } catch (error) {
      await this.emitEvent('products.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Sync customers from WooCommerce
   */
  async syncCustomers(): Promise<EcommerceCustomer[]> {
    try {
      const customers: EcommerceCustomer[] = [];
      let page = 1;
      const perPage = 100;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await this.makeRequest('GET', '/customers', {
          page,
          per_page: perPage
        });

        if (Array.isArray(response) && response.length > 0) {
          for (const wooCustomer of response) {
            const customer = this.transformWooCommerceCustomer(wooCustomer);
            customers.push(customer);
          }
          
          hasMorePages = response.length === perPage;
          page++;
        } else {
          hasMorePages = false;
        }
      }

      await this.emitEvent('customers.synced', { count: customers.length });
      return customers;
    } catch (error) {
      await this.emitEvent('customers.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Sync orders from WooCommerce
   */
  async syncOrders(dateRange?: { start: Date; end: Date }): Promise<EcommerceOrder[]> {
    try {
      const orders: EcommerceOrder[] = [];
      let page = 1;
      const perPage = 100;
      let hasMorePages = true;

      const params: any = {
        page,
        per_page: perPage,
        status: 'any'
      };

      if (dateRange) {
        params.after = dateRange.start.toISOString();
        params.before = dateRange.end.toISOString();
      }

      while (hasMorePages) {
        params.page = page;
        
        const response = await this.makeRequest('GET', '/orders', params);

        if (Array.isArray(response) && response.length > 0) {
          for (const wooOrder of response) {
            const order = this.transformWooCommerceOrder(wooOrder);
            orders.push(order);
          }
          
          hasMorePages = response.length === perPage;
          page++;
        } else {
          hasMorePages = false;
        }
      }

      await this.emitEvent('orders.synced', { count: orders.length });
      return orders;
    } catch (error) {
      await this.emitEvent('orders.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Get a specific product by ID
   */
  async getProduct(productId: string): Promise<EcommerceProduct | null> {
    try {
      const response = await this.makeRequest('GET', `/products/${productId}`);
      
      if (response && response.id) {
        return this.transformWooCommerceProduct(response);
      }
      
      return null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get a specific customer by ID
   */
  async getCustomer(customerId: string): Promise<EcommerceCustomer | null> {
    try {
      const response = await this.makeRequest('GET', `/customers/${customerId}`);
      
      if (response && response.id) {
        return this.transformWooCommerceCustomer(response);
      }
      
      return null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get a specific order by ID
   */
  async getOrder(orderId: string): Promise<EcommerceOrder | null> {
    try {
      const response = await this.makeRequest('GET', `/orders/${orderId}`);
      
      if (response && response.id) {
        return this.transformWooCommerceOrder(response);
      }
      
      return null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Update inventory for products
   */
  async updateInventory(updates: { productId: string; variantId?: string; inventory: number }[]): Promise<void> {
    try {
      for (const update of updates) {
        const updateData: any = {
          manage_stock: true,
          stock_quantity: update.inventory,
          stock_status: update.inventory > 0 ? 'instock' : 'outofstock'
        };

        if (update.variantId) {
          // Update product variation
          await this.makeRequest('PUT', `/products/${update.productId}/variations/${update.variantId}`, updateData);
        } else {
          // Update main product
          await this.makeRequest('PUT', `/products/${update.productId}`, updateData);
        }
      }

      await this.emitEvent('inventory.updated', { updates });
    } catch (error) {
      await this.emitEvent('inventory.update_failed', { 
        updates, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Get product recommendations based on context
   */
  async getRecommendations(context: RecommendationContext): Promise<EcommerceProduct[]> {
    try {
      // This is a simplified implementation
      // In a real scenario, you'd implement more sophisticated recommendation logic
      const products = await this.syncProducts();
      
      let recommendations = products;

      // Filter by category if specified
      if (context.category) {
        recommendations = recommendations.filter(product => 
          product.categories.includes(context.category!)
        );
      }

      // Filter by price range if specified
      if (context.priceRange) {
        recommendations = recommendations.filter(product => 
          product.price >= context.priceRange!.min && 
          product.price <= context.priceRange!.max
        );
      }

      // Sort by relevance (simplified - by total sales or rating)
      recommendations.sort((a, b) => {
        // Prioritize products with higher sales or better ratings
        return (b.customFields.totalSales || 0) - (a.customFields.totalSales || 0);
      });

      // Limit results
      recommendations = recommendations.slice(0, context.limit);

      await this.emitEvent('recommendations.generated', { 
        context, 
        count: recommendations.length 
      });

      return recommendations;
    } catch (error) {
      await this.emitEvent('recommendations.generation_failed', { 
        context, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Handle webhook from WooCommerce
   */
  async handleWebhook(topic: string, payload: any, headers: Record<string, string>): Promise<void> {
    try {
      // Verify webhook signature if secret is configured
      if (this.wooConfig.webhookSecret && !this.verifyWebhookSignature(payload, headers)) {
        throw new Error('Invalid webhook signature');
      }

      switch (topic) {
        case 'order.created':
          await this.handleOrderCreated(payload);
          break;
        case 'order.updated':
          await this.handleOrderUpdated(payload);
          break;
        case 'order.deleted':
          await this.handleOrderDeleted(payload);
          break;
        case 'customer.created':
          await this.handleCustomerCreated(payload);
          break;
        case 'customer.updated':
          await this.handleCustomerUpdated(payload);
          break;
        case 'customer.deleted':
          await this.handleCustomerDeleted(payload);
          break;
        case 'product.created':
          await this.handleProductCreated(payload);
          break;
        case 'product.updated':
          await this.handleProductUpdated(payload);
          break;
        case 'product.deleted':
          await this.handleProductDeleted(payload);
          break;
        default:
          console.log(`Unhandled webhook topic: ${topic}`);
      }

      await this.emitEvent('webhook.processed', { topic, payload });
    } catch (error) {
      await this.emitEvent('webhook.processing_failed', { 
        topic, 
        payload, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  // Private helper methods

  private async makeRequest(method: string, endpoint: string, data?: any): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Create basic auth header
    const auth = Buffer.from(`${this.wooConfig.consumerKey}:${this.wooConfig.consumerSecret}`).toString('base64');
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Basic ${auth}`
    };

    const requestConfig: any = {
      method: method as any,
      url,
      headers,
      timeout: this.wooConfig.timeout || 30000
    };

    if (data) {
      if (method === 'GET') {
        // For GET requests, add data as query parameters
        const params = new URLSearchParams(data);
        requestConfig.url += `?${params.toString()}`;
      } else {
        requestConfig.body = data;
      }
    }

    const response = await this.apiGateway.processRequest(requestConfig);
    return response.data;
  }

  private transformWooCommerceProduct(wooProduct: WooCommerceProduct): EcommerceProduct {
    return {
      id: wooProduct.id.toString(),
      title: wooProduct.name,
      description: wooProduct.description,
      price: parseFloat(wooProduct.price) || 0,
      compareAtPrice: wooProduct.regular_price && wooProduct.sale_price ? 
        parseFloat(wooProduct.regular_price) : undefined,
      currency: 'USD', // Would need to get from store settings
      sku: wooProduct.sku,
      inventory: wooProduct.stock_quantity || 0,
      images: wooProduct.images.map(img => ({
        id: img.id.toString(),
        url: img.src,
        altText: img.alt,
        position: img.position
      })),
      categories: wooProduct.categories.map(cat => cat.name),
      tags: wooProduct.tags.map(tag => tag.name),
      variants: [], // WooCommerce variations would need separate API call
      status: wooProduct.status === 'publish' ? 'active' : 'draft',
      createdAt: new Date(wooProduct.date_created),
      updatedAt: new Date(wooProduct.date_modified),
      customFields: {
        wooCommerceId: wooProduct.id,
        slug: wooProduct.slug,
        type: wooProduct.type,
        featured: wooProduct.featured,
        totalSales: wooProduct.total_sales,
        averageRating: parseFloat(wooProduct.average_rating) || 0,
        ratingCount: wooProduct.rating_count,
        stockStatus: wooProduct.stock_status,
        manageStock: wooProduct.manage_stock
      }
    };
  }

  private transformWooCommerceCustomer(wooCustomer: WooCommerceCustomer): EcommerceCustomer {
    const addresses = [];
    
    // Add billing address
    if (wooCustomer.billing.address_1) {
      addresses.push({
        id: 'billing',
        firstName: wooCustomer.billing.first_name,
        lastName: wooCustomer.billing.last_name,
        company: wooCustomer.billing.company,
        address1: wooCustomer.billing.address_1,
        address2: wooCustomer.billing.address_2,
        city: wooCustomer.billing.city,
        province: wooCustomer.billing.state,
        country: wooCustomer.billing.country,
        zip: wooCustomer.billing.postcode,
        phone: wooCustomer.billing.phone,
        isDefault: true
      });
    }

    // Add shipping address if different
    if (wooCustomer.shipping.address_1 && 
        wooCustomer.shipping.address_1 !== wooCustomer.billing.address_1) {
      addresses.push({
        id: 'shipping',
        firstName: wooCustomer.shipping.first_name,
        lastName: wooCustomer.shipping.last_name,
        company: wooCustomer.shipping.company,
        address1: wooCustomer.shipping.address_1,
        address2: wooCustomer.shipping.address_2,
        city: wooCustomer.shipping.city,
        province: wooCustomer.shipping.state,
        country: wooCustomer.shipping.country,
        zip: wooCustomer.shipping.postcode,
        phone: wooCustomer.billing.phone, // WooCommerce doesn't have shipping phone
        isDefault: false
      });
    }

    return {
      id: wooCustomer.id.toString(),
      email: wooCustomer.email,
      firstName: wooCustomer.first_name,
      lastName: wooCustomer.last_name,
      phone: wooCustomer.billing.phone,
      addresses,
      totalSpent: 0, // Would need to calculate from orders
      orderCount: 0, // Would need to calculate from orders
      averageOrderValue: 0, // Would need to calculate from orders
      lastOrderAt: undefined, // Would need to get from orders
      tags: [], // WooCommerce doesn't have customer tags by default
      acceptsMarketing: true, // Would need custom field or plugin
      createdAt: new Date(wooCustomer.date_created),
      updatedAt: new Date(wooCustomer.date_modified),
      customFields: {
        wooCommerceId: wooCustomer.id,
        username: wooCustomer.username,
        role: wooCustomer.role,
        isPayingCustomer: wooCustomer.is_paying_customer,
        avatarUrl: wooCustomer.avatar_url
      }
    };
  }

  private transformWooCommerceOrder(wooOrder: WooCommerceOrder): EcommerceOrder {
    return {
      id: wooOrder.id.toString(),
      orderNumber: wooOrder.number,
      customerId: wooOrder.customer_id.toString(),
      email: wooOrder.billing.email || '',
      totalPrice: parseFloat(wooOrder.total),
      subtotalPrice: parseFloat(wooOrder.total) - parseFloat(wooOrder.total_tax) - parseFloat(wooOrder.shipping_total),
      taxPrice: parseFloat(wooOrder.total_tax),
      shippingPrice: parseFloat(wooOrder.shipping_total),
      currency: wooOrder.currency,
      status: this.mapWooCommerceOrderStatus(wooOrder.status),
      lineItems: wooOrder.line_items.map(item => ({
        id: item.id.toString(),
        productId: item.product_id.toString(),
        variantId: item.variation_id > 0 ? item.variation_id.toString() : undefined,
        title: item.name,
        quantity: item.quantity,
        price: item.price,
        totalPrice: parseFloat(item.total),
        sku: item.sku
      })),
      shippingAddress: {
        id: 'shipping',
        firstName: wooOrder.shipping.first_name,
        lastName: wooOrder.shipping.last_name,
        company: wooOrder.shipping.company,
        address1: wooOrder.shipping.address_1,
        address2: wooOrder.shipping.address_2,
        city: wooOrder.shipping.city,
        province: wooOrder.shipping.state,
        country: wooOrder.shipping.country,
        zip: wooOrder.shipping.postcode,
        phone: wooOrder.billing.phone, // WooCommerce doesn't have shipping phone
        isDefault: false
      },
      billingAddress: {
        id: 'billing',
        firstName: wooOrder.billing.first_name,
        lastName: wooOrder.billing.last_name,
        company: wooOrder.billing.company,
        address1: wooOrder.billing.address_1,
        address2: wooOrder.billing.address_2,
        city: wooOrder.billing.city,
        province: wooOrder.billing.state,
        country: wooOrder.billing.country,
        zip: wooOrder.billing.postcode,
        phone: wooOrder.billing.phone,
        isDefault: true
      },
      createdAt: new Date(wooOrder.date_created),
      updatedAt: new Date(wooOrder.date_modified),
      customFields: {
        wooCommerceId: wooOrder.id,
        orderKey: wooOrder.order_key,
        paymentMethod: wooOrder.payment_method,
        paymentMethodTitle: wooOrder.payment_method_title,
        transactionId: wooOrder.transaction_id,
        customerNote: wooOrder.customer_note,
        datePaid: wooOrder.date_paid,
        dateCompleted: wooOrder.date_completed
      }
    };
  }

  private mapWooCommerceOrderStatus(status: string): 'pending' | 'paid' | 'fulfilled' | 'cancelled' | 'refunded' {
    switch (status) {
      case 'completed':
        return 'fulfilled';
      case 'processing':
      case 'on-hold':
        return 'paid';
      case 'cancelled':
        return 'cancelled';
      case 'refunded':
        return 'refunded';
      case 'failed':
      case 'trash':
        return 'cancelled';
      default:
        return 'pending';
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private verifyWebhookSignature(payload: any, headers: Record<string, string>): boolean {
    if (!this.wooConfig.webhookSecret) {
      return true; // Skip verification if no secret configured
    }

    const signature = headers['x-wc-webhook-signature'];
    if (!signature) {
      return false;
    }

    // Implement HMAC verification for WooCommerce webhooks
    // This would use crypto to verify the signature
    return true; // Simplified for now
  }

  private async registerWebhooks(): Promise<void> {
    if (!this.wooConfig.webhookEndpoint) {
      return; // Skip webhook registration if no endpoint configured
    }

    const webhooks = [
      { topic: 'order.created', name: 'Order Created' },
      { topic: 'order.updated', name: 'Order Updated' },
      { topic: 'order.deleted', name: 'Order Deleted' },
      { topic: 'customer.created', name: 'Customer Created' },
      { topic: 'customer.updated', name: 'Customer Updated' },
      { topic: 'customer.deleted', name: 'Customer Deleted' },
      { topic: 'product.created', name: 'Product Created' },
      { topic: 'product.updated', name: 'Product Updated' },
      { topic: 'product.deleted', name: 'Product Deleted' }
    ];

    try {
      for (const webhook of webhooks) {
        await this.makeRequest('POST', '/webhooks', {
          name: webhook.name,
          topic: webhook.topic,
          delivery_url: this.wooConfig.webhookEndpoint,
          secret: this.wooConfig.webhookSecret || ''
        });
      }

      await this.emitEvent('webhooks.registered', { count: webhooks.length });
    } catch (error) {
      await this.emitEvent('webhooks.registration_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  private async unregisterWebhooks(): Promise<void> {
    try {
      // Get all webhooks
      const webhooks = await this.makeRequest('GET', '/webhooks');
      
      // Delete webhooks that match our endpoint
      for (const webhook of webhooks) {
        if (webhook.delivery_url === this.wooConfig.webhookEndpoint) {
          await this.makeRequest('DELETE', `/webhooks/${webhook.id}`);
        }
      }

      await this.emitEvent('webhooks.unregistered', {});
    } catch (error) {
      await this.emitEvent('webhooks.unregistration_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  // Webhook event handlers

  private async handleOrderCreated(order: WooCommerceOrder): Promise<void> {
    const transformedOrder = this.transformWooCommerceOrder(order);
    await this.handlePurchase(transformedOrder);
    await this.emitEvent('order.created', { order: transformedOrder });
  }

  private async handleOrderUpdated(order: WooCommerceOrder): Promise<void> {
    const transformedOrder = this.transformWooCommerceOrder(order);
    
    // Check if order status changed to completed
    if (order.status === 'completed') {
      await this.handlePurchase(transformedOrder);
    }
    
    await this.emitEvent('order.updated', { order: transformedOrder });
  }

  private async handleOrderDeleted(order: WooCommerceOrder): Promise<void> {
    await this.emitEvent('order.deleted', { orderId: order.id.toString() });
  }

  private async handleCustomerCreated(customer: WooCommerceCustomer): Promise<void> {
    const transformedCustomer = this.transformWooCommerceCustomer(customer);
    await this.emitEvent('customer.created', { customer: transformedCustomer });
  }

  private async handleCustomerUpdated(customer: WooCommerceCustomer): Promise<void> {
    const transformedCustomer = this.transformWooCommerceCustomer(customer);
    await this.emitEvent('customer.updated', { customer: transformedCustomer });
  }

  private async handleCustomerDeleted(customer: WooCommerceCustomer): Promise<void> {
    await this.emitEvent('customer.deleted', { customerId: customer.id.toString() });
  }

  private async handleProductCreated(product: WooCommerceProduct): Promise<void> {
    const transformedProduct = this.transformWooCommerceProduct(product);
    await this.emitEvent('product.created', { product: transformedProduct });
  }

  private async handleProductUpdated(product: WooCommerceProduct): Promise<void> {
    const transformedProduct = this.transformWooCommerceProduct(product);
    
    // Handle inventory changes
    if (product.manage_stock && product.stock_quantity !== undefined) {
      await this.handleInventoryChange(product.id.toString(), undefined, product.stock_quantity);
    }
    
    await this.emitEvent('product.updated', { product: transformedProduct });
  }

  private async handleProductDeleted(product: WooCommerceProduct): Promise<void> {
    await this.emitEvent('product.deleted', { productId: product.id.toString() });
  }
}